/**
 * Dashboard card for one data-room category: progress bar, due-date urgency
 * badge, and a button that routes into the category.
 * Exports getUrgency so other views can reuse the same color/label logic.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

function parseLocalDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d); // local midnight — avoids UTC-shift on date-only strings
}

// Derives the card's status color, background, and label. A fully completed
// category is "complete" regardless of due date; otherwise the label reflects
// days until/past the deadline.
function getUrgency(dueDate, completedItems, totalItems) {
  if (completedItems >= totalItems) {
    return { status: 'complete', color: '#16a34a', bg: '#dcfce7', label: 'Complete' };
  }
  if (!dueDate) {
    return { status: 'none', color: '#9ca3af', bg: '#f3f4f6', label: null };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = parseLocalDate(dueDate);
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));

  if (diff < 0)  return { status: 'overdue', color: '#dc2626', bg: '#fee2e2', label: `${Math.abs(diff)}d overdue` };
  if (diff === 0) return { status: 'today',  color: '#ea580c', bg: '#fff7ed', label: 'Due today' };
  if (diff <= 7)  return { status: 'urgent', color: '#d97706', bg: '#fef3c7', label: `Due in ${diff}d` };
  if (diff <= 21) return { status: 'soon',   color: '#ca8a04', bg: '#fefce8', label: `Due in ${diff}d` };

  const label = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { status: 'ok', color: '#a16207', bg: '#fefce8', label };
}

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={styles.track}>
      <div style={{ ...styles.fill, width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function CategoryCard({ category }) {
  const navigate = useNavigate();
  const { id, name, completedItems, totalItems, dueDate } = category;
  const urgency = getUrgency(dueDate, completedItems, totalItems);

  return (
    <div style={{ ...styles.card, borderLeft: `4px solid ${urgency.color}` }}>
      <div style={styles.body}>
        <div style={styles.titleRow}>
          <h2 style={styles.title}>{id}. {name}</h2>
          {urgency.label && (
            <span style={{ ...styles.badge, background: urgency.bg, color: urgency.color }}>
              {urgency.label}
            </span>
          )}
        </div>
        <ProgressBar value={completedItems} max={totalItems} color={urgency.color} />
        <p style={styles.meta}>{completedItems} of {totalItems} completed</p>
      </div>
      <button
        style={styles.arrow}
        onClick={() => navigate(`/data-room/${id}`)}
        aria-label={`Open ${name}`}
      >
        &#8594;
      </button>
    </div>
  );
}

export { getUrgency };

const styles = {
  card: {
    display: 'flex',
    alignItems: 'stretch',
    background: '#fff',
    border: '1px solid #e8e8e8',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    overflow: 'hidden',
    marginBottom: 'var(--space-4)',
  },
  body: {
    flex: 1,
    padding: 'var(--space-5) var(--space-5) var(--space-4)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 'var(--space-3)',
    flexWrap: 'wrap',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 18,
    fontWeight: 800,
    color: 'var(--color-forest)',
    lineHeight: 1.2,
  },
  badge: {
    padding: '3px 10px',
    borderRadius: 'var(--radius-full)',
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: '0.03em',
    flexShrink: 0,
  },
  track: {
    height: 7,
    background: '#ebebeb',
    borderRadius: 'var(--radius-full)',
    overflow: 'hidden',
    marginBottom: 'var(--space-3)',
  },
  fill: {
    height: '100%',
    borderRadius: 'var(--radius-full)',
    transition: 'width 0.4s ease',
  },
  meta: {
    fontSize: 16,
    fontWeight: 500,
    color: 'var(--color-forest)',
    opacity: 0.65,
  },
  arrow: {
    width: 52,
    flexShrink: 0,
    background: 'var(--color-rust)',
    color: '#fff',
    fontSize: 20,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
  },
};
